import type { Permission } from "@pax/api-contract/common";
import { ALL_MODERATION_PERMISSIONS } from "@pax/api-contract/moderation/v1alpha1";
import { HapticSurface, panelRow, panelRowQuiet } from "@pax/ui";
import { Link } from "@tanstack/react-router";
import {
  BookText,
  Bot,
  ChartColumn,
  Coins,
  Compass,
  FlaskConical,
  Gamepad2,
  Images,
  type LucideIcon,
  Search,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";
import { Soon } from "@/components/Soon";
import { useMyPermissions } from "@/features/auth/permissions";
import { useSession } from "@/features/auth/queries";
import { useHasEarnings } from "@/features/earnings/queries";

type Way = {
  label: string;
  icon: LucideIcon;
  /** Left off where the destination has a place here but no screen yet. */
  to?:
    | "/"
    | "/games"
    | "/earnings"
    | "/search"
    | "/assets"
    | "/agents"
    | "/evals"
    | "/ledger"
    | "/models"
    | "/moderation"
    | "/staff";
  /** A staff tool, shown only to people who hold one of these permissions. */
  permissions?: readonly Permission[];
  /** Shown only to people who have been paid a creator reward. */
  earners?: boolean;
  exact?: boolean;
};

const WAYS: Way[] = [
  { label: "My games", icon: Gamepad2, to: "/games" },
  { label: "Preset earnings", icon: Coins, to: "/earnings", earners: true },
  { label: "Explore", icon: Compass, to: "/", exact: true },
  { label: "Search", icon: Search, to: "/search" },
  { label: "Assets", icon: Images, to: "/assets" },
  { label: "Community", icon: Users },
  { label: "Evals", icon: FlaskConical, to: "/evals", permissions: ["agents.manage"] },
  { label: "Agents", icon: Bot, to: "/agents", permissions: ["agents.manage"] },
  { label: "Models", icon: ChartColumn, to: "/models", permissions: ["agents.manage"] },
  {
    label: "Moderation",
    icon: ShieldCheck,
    to: "/moderation",
    permissions: [...ALL_MODERATION_PERMISSIONS, "accounts.ban"],
  },
  { label: "Ledger", icon: BookText, to: "/ledger", permissions: ["credits.view"] },
  { label: "Manage staff", icon: UserCog, to: "/staff", permissions: ["staff.manage"] },
];

function Face({ icon: Icon, label, soon }: { icon: LucideIcon; label: string; soon?: boolean }) {
  return (
    <>
      <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {soon && <Soon />}
    </>
  );
}

/**
 * The destinations, and nothing else: no picture, no settings, no sign out.
 * Those are yours rather than places, and they live in your account.
 */
export function Ways({ onNavigate }: { onNavigate?: () => void }) {
  const { data: session } = useSession();
  const { permissions } = useMyPermissions(session?.user);
  const hasEarnings = useHasEarnings(session?.user);
  const ways = WAYS.filter(
    way =>
      (!way.permissions || way.permissions.some(permission => permissions.includes(permission))) &&
      (!way.earners || hasEarnings),
  );
  const firstStaffWay = ways.find(way => way.permissions);
  return (
    <nav className="flex flex-1 flex-col gap-0.5 p-2 pt-0">
      {ways.map(way =>
        way.to ? (
          <Link
            key={way.label}
            to={way.to}
            activeOptions={{ exact: way.exact }}
            onClick={onNavigate}
            className={`${panelRow} text-muted-foreground hover:text-foreground ${way === firstStaffWay ? "mt-7" : ""}`}
            activeProps={{ className: "bg-foreground/10 font-medium text-foreground" }}
          >
            <Face icon={way.icon} label={way.label} />
            <HapticSurface />
          </Link>
        ) : (
          <div key={way.label} className={`${panelRowQuiet} text-muted-foreground`}>
            <Face icon={way.icon} label={way.label} soon />
          </div>
        ),
      )}
    </nav>
  );
}
